import { useTranslation } from 'react-i18next';
import { Box, DialogContentText } from '@mui/material';
import React from 'react';

import { AlertDialog, AlertDialogProps } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import {
    IContentTypeValue,
    ISkillListItemFragment,
    useCreatePortfolioItemMutation,
    useSkillGroupsQuery,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { Link } from 'common/components/Link';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { Divider } from 'common/components/Divider';
import { Typography } from 'common/components/Typography';

interface IProps extends AlertDialogProps {
    loading?: boolean;
    skill: ISkillListItemFragment;
    userId: string;
    userName: string;
    onCancel?: () => void;
    onClose?: () => void;
    onUpdate?: (portfolioItemId?: string) => void;
}

export const CreateSkillPortfolioItemDialog = ({
    loading,
    skill,
    userId,
    userName,
    open,
    onCancel,
    onClose,
    onUpdate,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    const { id: skillId, name: skillName } = skill;

    const { data, loading: skillLoading } = useSkillGroupsQuery({
        variables: { id: skillId },
        skip: !open,
    });

    const [createPortfolioItem, { loading: createPortfolioItemLoading }] =
        useCreatePortfolioItemMutation();

    const dialogLoading = createPortfolioItemLoading || skillLoading || loading;

    const groups = data?.skill?.groups;

    const createItem = async () => {
        try {
            const response = await createPortfolioItem({
                variables: {
                    portfolioItem: {
                        title: skillName,
                        userId,
                        relatedObjectId: skillId,
                        relatedObjectType: IContentTypeValue.Skill,
                    },
                },
            });

            const skillPortfolioItemId =
                response.data?.createPortfolioItem.portfolioItem.id;

            onUpdate?.(skillPortfolioItemId);
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('skillPortfolioItemActionSuccessMessage', {
                userName: userName,
            }),
            {
                variant: 'success',
            }
        );

        onClose?.();
    };

    const buttons = !skillLoading ? (
        <>
            <Button
                autoFocus
                color="primary"
                loading={dialogLoading}
                variant="contained"
                onClick={createItem}
            >
                {translate('createSkillPortfolioItemDialog.confirmButton')}
            </Button>

            {onCancel && (
                <Button
                    disabled={dialogLoading}
                    variant="contained"
                    onClick={onCancel}
                >
                    {translate('cancel')}
                </Button>
            )}
        </>
    ) : undefined;

    const hasGroups = !!groups?.length;

    return (
        <AlertDialog
            actions={buttons}
            open={open}
            title={translate('createSkillPortfolioItemDialog.title')}
            onClose={onClose}
            {...other}
        >
            {skillLoading && (
                <Box sx={{ position: 'relative', height: '50px', my: 2 }}>
                    <Loader />
                </Box>
            )}

            {!skillLoading && (
                <DialogContentText
                    color="text.primary"
                    component="div"
                    sx={{ whiteSpace: 'pre-line', mb: 1 }}
                    variant="body2"
                >
                    <Box sx={{ mb: hasGroups ? 2 : 0 }}>
                        {translate('createSkillPortfolioItemDialog.text')}
                    </Box>

                    {hasGroups && (
                        <>
                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="h4">
                                {translate(
                                    'createSkillPortfolioItemDialog.groupsTitle'
                                )}
                            </Typography>

                            {groups.map(({ id, name }, index) => (
                                <React.Fragment key={id}>
                                    <Link
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                        }}
                                        onClick={() => {
                                            onClose?.();

                                            openGlobalDrawer({
                                                type: IGlobalDrawerType.Group,
                                                itemId: id,
                                                props: {
                                                    onClose: () => {
                                                        closeGlobalDrawer({
                                                            type: IGlobalDrawerType.Group,
                                                        });
                                                    },
                                                    onUserUpdate: () => {
                                                        closeGlobalDrawer({
                                                            type: IGlobalDrawerType.Group,
                                                        });

                                                        onUpdate?.();
                                                    },
                                                },
                                            });
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {index < groups.length - 1 && ', '}
                                </React.Fragment>
                            ))}
                        </>
                    )}

                    <Divider sx={{ mt: 2 }} />
                </DialogContentText>
            )}
        </AlertDialog>
    );
};
